/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /** @type {string[]} Last usable history, also used when storage is unavailable. */
  static #memory = [];
  static #memoryOnly = false;

  /** @param {unknown} value @returns {string | null} */
  static #productId(value) {
    if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
    return typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
  }

  /** @param {unknown} value @returns {string[]} */
  static #normalize(value) {
    if (!Array.isArray(value)) return [];
    const products = [];
    for (const entry of value) {
      const id = this.#productId(entry);
      if (!id || products.includes(id)) continue;
      products.push(id);
      if (products.length === this.#MAX_PRODUCTS) break;
    }
    return products;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = this.#productId(productId);
    if (!id) return;
    const viewedProducts = this.getProducts().filter((entry) => entry !== id);
    viewedProducts.unshift(id);
    this.#memory = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    if (this.#memoryOnly) return;
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(this.#memory));
    } catch {
      // Do not let a later read of stale persisted data overwrite this selection.
      this.#memoryOnly = true;
    }
  }

  static clearProducts() {
    this.#memory = [];
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      this.#memoryOnly = true;
    }
  }

  /**
   * Retrieves a safe copy of the viewed products, falling back to page memory.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (this.#memoryOnly) return [...this.#memory];

    let stored;
    try {
      stored = localStorage.getItem(this.#STORAGE_KEY);
    } catch {
      this.#memoryOnly = true;
      return [...this.#memory];
    }

    try {
      this.#memory = this.#normalize(JSON.parse(stored || '[]'));
    } catch {
      this.#memory = [];
    }
    return [...this.#memory];
  }
}
